import styled from 'styled-components'
import { IconButton, SelectChangeEvent, Typography } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import Select from '../../../../components/editors/select/Select'
import { ReactNode, memo, useCallback } from 'react'
import { SelectOption } from '../../Kanban'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'

const OptionLabel = ({ option }: { option: SelectOption }) => (
  <OptionLabelContainer>
    <span>{option.name}</span>
    {option.subText && <SubText>{option.subText}</SubText>}
  </OptionLabelContainer>
)

const OptionLabelContainer = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const SubText = styled(Typography)({
  fontSize: '11px',
  color: colorPalette.monotone[3],
  lineHeight: '1 !important',
})

const SearchConditionSelect = memo(
  ({
    value,
    options,
    onChange,
    onOpen,
    onClose,
  }: {
    value?: string
    options: SelectOption[]
    onChange: (value?: string) => void
    onOpen?: () => void
    onClose?: () => void
  }) => {
    const onChangeValue = useCallback(
      (e: SelectChangeEvent<unknown>, child: ReactNode) => {
        onChange(
          typeof e.target.value === 'string'
            ? e.target.value
            : (e.target.value as SelectOption)?.value
        )
      },
      [onChange]
    )
    const clear = useCallback(() => {
      onChange('')
    }, [onChange])

    // MUIのSelectはvalueにnullを許容せず警告が出るため、空文字を使用
    // valueはUUID文字列のため、0やfalseになることはない
    return (
      <StyledSelect
        isSelected={!!value}
        value={value ?? ''}
        onChange={onChangeValue}
        onOpen={onOpen}
        onClose={onClose}
        variant="standard"
        options={options}
        getOptionValue={o => o.value}
        getOptionLabel={o => <OptionLabel option={o} />}
        getOptionIconUrl={o => o.iconUrl}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
        endAdornment={
          value ? (
            <IconButton
              onClick={clear}
              aria-label="clear"
              size="small"
              edge="end"
            >
              <ClearRoundedIcon />
            </IconButton>
          ) : (
            <></>
          )
        }
      />
    )
  },
  (prev, next) =>
    prev.value === next.value &&
    prev.options === next.options &&
    prev.onOpen === next.onOpen &&
    prev.onClose === next.onClose
)

export default SearchConditionSelect

const StyledSelect = styled(Select)<{ isSelected: boolean }>(props => ({
  display: 'flex',
  minWidth: '250px',
  minHeight: '32px',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  '& .MuiSelect-select': {
    display: 'inline-flex',
    paddingLeft: '8px',
    minHeight: '32px',
  },
  '& .MuiAvatar-root': {
    marginLeft: '0',
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  '& .MuiTypography-root': {
    lineHeight: '20px !important',
    color: colorPalette.monotone[5],
  },
  '& .MuiSelect-icon': {
    display: props.isSelected ? 'none' : 'inline-flex',
  },
}))
